import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  TextField,
  MenuItem,
  InputAdornment,
  SvgIcon,
  Select,
  Button,
  FormControlLabel,
  Switch,
  Checkbox,
  ListItemText,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Stack,
  Box,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GetStockResponse } from '../../../app/services/api/generated';
import { StockFilterOptions } from './interfaces/StockFilterOptions';
import StockOnHandTable from './StockOnHandTable';
import { SearchSm } from '../../../assets';
import { UserContext } from '../../../components/shared/useUser';
import useLogError from '../../../hooks/useLogError';
import { Section } from '../../../components/ui/Section';
import { handleSearchFieldKeyDown } from '../../../utils';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { ConfigurationName } from '../../../enums/ConfigurationName';
import { useConfigurations } from '../../../components/shared/useConfigurations';

const StockOnHandBox = (props: {
  stockOnHands: GetStockResponse,
  loadData: (options: StockFilterOptions) => void;
  isLoading: boolean;
}) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const { logError } = useLogError();
  const { getAvailableCountZones, getCountZoneTypes } = useNswagClient();
  const { isConfigurationEnabled } = useConfigurations();
  const isCountZonesEnabled = isConfigurationEnabled(ConfigurationName.CountZonesWithItemsEnabled);
  const defaultSort = 'itemNumber';
  const defaultTake = 10;
  const defaultSkip = 0;
  const defaultDirection = 'desc';

  const { user, hasPermissionTo, selectedStore } = useContext(UserContext);
  const [page, setPage] = useState(0);
  const [categoryFilters, setCategoryFilters] = useState('');
  const [stockFilters, setStockFilters] = useState('');
  const [storeFilters, setStoreFilters] = useState('');
  const [countZoneFilters, setCountZoneFilters] = useState('');
  const [countZoneTypeFilters, setCountZoneTypeFilters] = useState('');
  const [category, setCategory] = React.useState<string[]>([]);
  const [stores, setStores] = React.useState<string[]>([]);
  const [stock, setStock] = React.useState<string[]>([]);
  const [countZones, setCountZones] = React.useState<string[]>([]);
  const [countZoneTypes, setCountZoneTypes] = React.useState<string[]>([]);
  const [allCountZones, setAllCountZones] = React.useState<{ id: number; name: string; typeId: number | null }[]>([]);
  const [availableCountZoneTypes, setAvailableCountZoneTypes] = React.useState<{ id: number; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [lastSearch, setLastSearch] = useState('');
  const [hasRecentActivity, setHasRecentActivity] = useState(true);
  const [isMadeInStore, setIsMadeInStore] = useState<boolean>(false);
  const [isOrderableQuantity, setIsOrderableQuantity] = useState<boolean>(true);
  const isMultiStore = storeFilters.split(',').filter(Boolean).length > 1;
  const hasPermissionToReadMadeInStore = hasPermissionTo(['MadeInStoreRead']);

  const availableCountZones = React.useMemo(() => {
    if (!countZoneTypeFilters) return allCountZones;
    const selectedTypeIds = countZoneTypeFilters.split(',').filter(Boolean).map(Number);
    if (selectedTypeIds.length === 0) return allCountZones;
    return allCountZones.filter((z) => z.typeId != null && selectedTypeIds.includes(z.typeId));
  }, [allCountZones, countZoneTypeFilters]);

  const effectiveZoneTypeFilters = countZones.includes('-1') ? '' : countZoneTypeFilters;

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const stockLevelOptions = [
    { id: 'positivestock', name: t('stockOnHand.positiveStock') },
    { id: 'negativestock', name: t('stockOnHand.negativeStock') },
    { id: 'belowminimumquantity', name: t('stockOnHand.belowMinimumQuantity') },
    { id: 'outofstock', name: t('stockOnHand.outOfStock') },
  ];
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSearch(newSearchValue);
  };

  const handleCategoryChange = (event: SelectChangeEvent<typeof category>) => {
    const {
      target: { value },
    } = event;

    setCategory(typeof value === 'string' ? value.split(',') : value);
    setCategoryFilters(typeof value === 'string' ? value : value.join(','));
  };

  const handleStoreChange = (event: SelectChangeEvent<typeof stores>) => {
    const {
      target: { value },
    } = event;
    setStores(typeof value === 'string' ? value.split(',') : value);

    if (typeof value === 'string') {
      setStoreFilters(value);
    } else {
      const storeKeys = value
        .map((item) => user?.stores?.find((x) => x.storeName === item)?.storeNumber)
        .filter((key) => key !== undefined);

      setStoreFilters(storeKeys.join(','));
    }
  };

  const handleStockLevelChange = (event: SelectChangeEvent<typeof stock>) => {
    const {
      target: { value },
    } = event;
    setStock(typeof value === 'string' ? value.split(',') : value);

    if (typeof value === 'string') {
      setStockFilters(value);
    }
    else {
      const stockLevelIds = value
        .map((item) => stockLevelOptions.find((x) => x.name === item)?.id)
        .filter((id) => id !== undefined);
      setStockFilters(stockLevelIds.join(','));
    }
  };

  const handleRecentActivity = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHasRecentActivity(event.target.checked);
  };

  const handleIsMadeInStore = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsMadeInStore(event.target.checked);
  };

  const handleCountZoneChange = (event: SelectChangeEvent<typeof countZones>) => {
    const {
      target: { value },
    } = event;
    const selected = typeof value === 'string' ? value.split(',') : value;
    setCountZones(selected);

    const filterValues = selected.map((s) => {
      const zone = availableCountZones.find((z) => z.name === s);
      return zone ? String(zone.id) : s;
    });
    setCountZoneFilters(filterValues.join(','));

    if (selected.length === 1 && selected[0] === '-1') {
      setCountZoneTypes([]);
      setCountZoneTypeFilters('');
    }
  };

  const handleCountZoneTypeChange = (event: SelectChangeEvent<typeof countZoneTypes>) => {
    const {
      target: { value },
    } = event;
    const selected = typeof value === 'string' ? value.split(',') : value;
    setCountZoneTypes(selected);

    const ids = selected
      .map((name) => availableCountZoneTypes.find((t) => t.name === name)?.id)
      .filter((id): id is number => id != null);
    setCountZoneTypeFilters(ids.join(','));

    setCountZones([]);
    setCountZoneFilters('');
  };

  useEffect(() => {
    if (!isCountZonesEnabled) return;

    const storeNumbersForZones = storeFilters
      ? storeFilters.split(',').filter(Boolean)
      : selectedStore?.storeNumber ? [selectedStore.storeNumber] : [];

    if (storeNumbersForZones.length === 0) {
      setAllCountZones([]);
      setAvailableCountZoneTypes([]);
      setCountZones([]);
      setCountZoneFilters('');
      setCountZoneTypes([]);
      setCountZoneTypeFilters('');
      return;
    }

    const isMultiStore = storeNumbersForZones.length > 1;

    const uniqueFranchiseStores = storeNumbersForZones.reduce<string[]>((acc, sn) => {
      const store = user?.stores?.find((s) => s.storeNumber === sn);
      const fid = store?.franchiseId;
      if (fid != null && !acc.some((existing) => user?.stores?.find((s) => s.storeNumber === existing)?.franchiseId === fid)) {
        acc.push(sn);
      }
      return acc;
    }, []);

    const storesToFetchTypes = uniqueFranchiseStores.length > 0
      ? uniqueFranchiseStores
      : selectedStore?.storeNumber ? [selectedStore.storeNumber] : [];

    Promise.all(storesToFetchTypes.map((sn) => getCountZoneTypes(sn)))
      .then((results) => {
        const seen = new Set<number>();
        const types: { id: number; name: string }[] = [];
        for (const t of results.flat()) {
          if (t.id != null && t.name && !seen.has(t.id)) {
            seen.add(t.id);
            types.push({ id: t.id, name: t.name });
          }
        }
        setAvailableCountZoneTypes(types);

        const availableNames = new Set(types.map((t) => t.name));
        setCountZoneTypes((prev) => {
          const kept = prev.filter((name) => availableNames.has(name));
          if (kept.length !== prev.length) {
            const keptIds = kept
              .map((name) => types.find((t) => t.name === name)?.id)
              .filter((id): id is number => id != null);
            setCountZoneTypeFilters(keptIds.join(','));
          }
          return kept.length !== prev.length ? kept : prev;
        });
      })
      .catch((error) => logError(error));

    if (isMultiStore) {
      setAllCountZones([]);
      setCountZones([]);
      setCountZoneFilters('');
    } else {
      getAvailableCountZones(storeNumbersForZones)
        .then((result) => {
          const zones = (result?.countZones ?? [])
            .filter((z) => z.id != null && z.name)
            .map((z) => ({
              id: z.id!,
              name: z.name!,
              typeId: z.countZoneTypeId ?? null,
            }));
          setAllCountZones(zones);

          const availableZoneNames = new Set(zones.map((z) => z.name));
          availableZoneNames.add('-1');
          setCountZones((prev) => {
            const kept = prev.filter((name) => availableZoneNames.has(name));
            if (kept.length !== prev.length) {
              const filterValues = kept.map((s) => {
                const zone = zones.find((z) => z.name === s);
                return zone ? String(zone.id) : s;
              });
              setCountZoneFilters(filterValues.join(','));
            }
            return kept.length !== prev.length ? kept : prev;
          });
        })
        .catch((error) => logError(error));
    }
  }, [storeFilters, selectedStore?.storeNumber, isCountZonesEnabled]);

  const callLoadDataFromAPI = (options: StockFilterOptions) => {
    props.loadData(options);
  };

  const loadDataFromOrderTable = (fieldToSort: string, sortDirection: string, skip: number, take: number) => {
    callLoadDataFromAPI({
      search,
      fieldToSort,
      sortDirection,
      skip,
      take,
      filters: stockFilters,
      categoryFilters,
      storeFilters,
      hasRecentActivity,
      isMadeInStore,
      countZoneFilters,
      countZoneTypeFilters: effectiveZoneTypeFilters,
    });
  };

  const clearAll = () => {
    setPage(1);
    setCategoryFilters('');
    setCategory([]);
    setStock([]);
    setStockFilters('');
    setStoreFilters('');
    setStores([]);
    setCountZones([]);
    setCountZoneFilters('');
    setCountZoneTypes([]);
    setCountZoneTypeFilters('');
    setHasRecentActivity(false);
    setSearch('');

    callLoadDataFromAPI({
      search: '',
      fieldToSort: defaultSort,
      sortDirection: defaultDirection,
      skip: defaultSkip,
      take: defaultTake,
      filters: undefined,
      categoryFilters: undefined,
      storeFilters: undefined,
      hasRecentActivity: undefined,
      isMadeInStore: undefined,
      countZoneFilters: undefined,
      countZoneTypeFilters: undefined,
    });
  };

  const storeNameLookup = React.useMemo(() => {
    const lookup: Record<string, string> = {};
    user?.stores?.forEach((s) => {
      if (s.storeNumber && s.storeName) lookup[s.storeNumber] = s.storeName;
    });
    return lookup;
  }, [user?.stores]);

  const searchCall = () => {
    setPage(1);
    setLastSearch(search);
    callLoadDataFromAPI({
      search,
      fieldToSort: undefined,
      sortDirection: undefined,
      skip: defaultSkip,
      take: defaultTake,
      filters: stockFilters,
      categoryFilters,
      storeFilters,
      hasRecentActivity,
      isMadeInStore,
      countZoneFilters,
      countZoneTypeFilters: effectiveZoneTypeFilters,
    });
  };

  return (
    <Grid container
      spacing={5}
      sx={{ pt: '6px' }}>
      <Grid item
        xs={12} >
        <Stack direction={{ sm: 'row', xs: 'column' }}
          width='100%'
          spacing={5}
          alignItems={{ xs: 'stretch', sm: 'center' }}>
          <Box flexGrow={1}>
            <FormControl fullWidth>
              <TextField
                label={t('stockOnHand.itemNumberOrDescription')}
                size="small"
                id="input-with-icon-textfield"
                variant="outlined"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SvgIcon>
                        <SearchSm />
                      </SvgIcon>
                    </InputAdornment>
                  ),
                }}
                value={search}
                onChange={handleSearchChange}
                onKeyDown={e => handleSearchFieldKeyDown(e, searchCall)}
              />
            </FormControl>
          </Box>

          <FormControlLabel
            control={
              <Switch checked={hasRecentActivity}
                onChange={handleRecentActivity} />
            }
            label={t('stockOnHand.recentActivity')}
            labelPlacement="start"
          />

          {hasPermissionToReadMadeInStore && (
            <FormControlLabel
              control={
                <Switch checked={isMadeInStore}
                  onChange={handleIsMadeInStore} />
              }
              label={t('stockOnHand.madeInStore')}
              labelPlacement="start"
            />
          )}
          <FormControlLabel
            control={
              <Switch checked={isOrderableQuantity}
                onChange={(_, checked) => setIsOrderableQuantity(checked)} />
            }
            label={t('stockOnHand.orderableUnits')}
            labelPlacement="start"
          />
        </Stack>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="stock-stock-level-label">{t('stockOnHand.stockLevel')}</InputLabel>
          <Select
            labelId="stock-stock-level-label"
            id="stock-stock-level"
            size='small'
            label={t('stockOnHand.stockLevel')}
            multiple
            value={stock}
            onChange={handleStockLevelChange}
            renderValue={(selected) => selected.toString()}
            MenuProps={MenuProps}
          >
            {stockLevelOptions.map((option) => (
              <MenuItem key={option.name}
                value={option.name}>
                <Checkbox checked={stock.some(stockLevel =>
                  stockLevel === option.name)} />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="category-multiple-checkbox-label">{t('stockOnHand.category')}</InputLabel>
          <Select
            labelId="category-multiple-checkbox-label"
            id="category-multiple-checkbox"
            size='small'
            label={t('stockOnHand.category')}
            multiple
            value={category}
            onChange={handleCategoryChange}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {props.stockOnHands?.categories?.filter((name) => name !== null).map((name) => (
              <MenuItem key={name}
                value={name}>
                <Checkbox checked={category.indexOf(name) > -1} />
                <ListItemText primary={name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}
        md={4}>
        <FormControl fullWidth
          size='small'>
          <InputLabel id="store-multiple-checkbox-label">{t('Store')}</InputLabel>
          <Select
            labelId="store-multiple-checkbox-label"
            id="store-multiple-checkbox"
            size='small'
            label={t('Store')}
            multiple
            value={stores}
            onChange={handleStoreChange}
            renderValue={(selected) => selected.join(', ')}
            MenuProps={MenuProps}
          >
            {user?.stores?.map((store) => (
              <MenuItem key={store.storeNumber}
                value={store.storeName}>
                <Checkbox checked={stores.some(s =>
                  s === store.storeName)} />
                <ListItemText primary={store.storeName} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      {isCountZonesEnabled && (
        <Grid item
          xs={12}
          md={4}>
          <FormControl fullWidth
            size='small'>
            <InputLabel id="count-zone-type-multiple-checkbox-label">{t('stockOnHand.countZoneType')}</InputLabel>
            <Select
              labelId="count-zone-type-multiple-checkbox-label"
              id="count-zone-type-multiple-checkbox"
              size='small'
              label={t('stockOnHand.countZoneType')}
              multiple
              value={countZoneTypes}
              onChange={handleCountZoneTypeChange}
              renderValue={(selected) => selected.join(', ')}
              MenuProps={MenuProps}
            >
              {availableCountZoneTypes.length === 0 ? (
                <MenuItem disabled>
                  <ListItemText primary={t('stockOnHand.noCountZoneTypes')} />
                </MenuItem>
              ) : (
                availableCountZoneTypes.map((type) => (
                  <MenuItem key={type.id}
                    value={type.name}>
                    <Checkbox checked={countZoneTypes.indexOf(type.name) > -1} />
                    <ListItemText primary={type.name} />
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </Grid>
      )}
      {isCountZonesEnabled && !isMultiStore && (
        <Grid item
          xs={12}
          md={4}>
          <FormControl fullWidth
            size='small'>
            <InputLabel id="count-zone-multiple-checkbox-label">{t('stockOnHand.countZone')}</InputLabel>
            <Select
              labelId="count-zone-multiple-checkbox-label"
              id="count-zone-multiple-checkbox"
              size='small'
              label={t('stockOnHand.countZone')}
              multiple
              value={countZones}
              onChange={handleCountZoneChange}
              renderValue={(selected) => selected.map((s) => s === '-1' ? t('stockOnHand.notAssignedToCountZone') : s).join(', ')}
              MenuProps={MenuProps}
            >
              <MenuItem value="-1">
                <Checkbox checked={countZones.indexOf('-1') > -1} />
                <ListItemText primary={t('stockOnHand.notAssignedToCountZone')} />
              </MenuItem>
              {availableCountZones.map((zone) => (
                <MenuItem key={zone.name}
                  value={zone.name}>
                  <Checkbox checked={countZones.indexOf(zone.name) > -1} />
                  <ListItemText primary={zone.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      )}
      {isCountZonesEnabled && isMultiStore && (
        <Grid item
          xs={12}
          md={4} />
      )}

      <Grid item
        xs={12}
        sm={6}
        md={6}>
        <Button
          fullWidth
          variant="secondary"
          size="lg"
          onClick={clearAll}
        >
          {t('clearAll')}
        </Button>
      </Grid>
      <Grid item
        xs={12}
        sm={6}
        md={6}>
        <Button
          fullWidth
          variant="primary"
          size="lg"
          onClick={searchCall}
          startIcon={<SearchSm />}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <StockOnHandTable isLoading={props.isLoading}
            list={props.stockOnHands}
            isOrderableQuantity={isOrderableQuantity}
            isCountZonesEnabled={isCountZonesEnabled}
            setPageZero={page}
            loadData={loadDataFromOrderTable}
            clearAll={clearAll}
            lastSearch={lastSearch}
            storeNameLookup={storeNameLookup} />
        </Section>
      </Grid>
    </Grid>);
};

StockOnHandBox.displayName = 'StockCountDetails';
export default StockOnHandBox;

